import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Wand2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

export const RequestStockDialog = ({ onClose, onSent }) => {
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/stock-requests/head-products', { params: { q: query } })
        .then(r => setProducts(r.data.products)).catch(e => toast.error(e?.response?.data?.detail || 'Product search failed'));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const add = p => setLines(l => (l.some(i => i.product_id === p.product_id) || l.length >= 100 ? l : [...l, { ...p, quantity: 1 }]));
  const invalid = lines.some(i => !Number.isInteger(Number(i.quantity)) || Number(i.quantity) < 1 || i.quantity === '');

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get('/stock-requests/suggestions');
      const items = data.suggestions || [];
      if (!items.length) { toast.info('Nothing to suggest right now — stock looks healthy.'); return; }
      setLines(items.slice(0, 100).map(s => ({ product_id: s.product_id, name: s.name, head_available: s.head_available, quantity: s.quantity })));
      toast.success(`Pre-filled ${items.length} item${items.length > 1 ? 's' : ''} from low stock and recent sales`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not build suggestions'); }
    finally { setSuggesting(false); }
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post('/stock-requests', { items: lines.map(i => ({ product_id: i.product_id, quantity: Number(i.quantity) })), note });
      toast.success('Request sent to your head location');
      onSent(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the request'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && !busy && onClose()}>
      <DialogContent className="transfer-dialog" data-testid="request-stock-dialog">
        <DialogHeader>
          <DialogTitle>Request stock from head</DialogTitle>
          <DialogDescription>Pick what you need and how much. The owner sends it as a transfer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Button type="button" variant="secondary" className="w-full" onClick={suggest} disabled={suggesting || busy} data-testid="request-stock-suggest">
            {suggesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}Suggest what we need
          </Button>
          <div className="space-y-1">
            <Label htmlFor="request-stock-search">Head products</Label>
            <Input id="request-stock-search" placeholder="Search products" value={query} onChange={e => setQuery(e.target.value)} data-testid="request-stock-search" />
          </div>
          <div className="max-h-40 overflow-y-auto divide-y border rounded-md" data-testid="request-stock-products">
            {!products.length ? <p className="p-3 text-sm text-muted-foreground">No matching products.</p> : products.map(p => (
              <div key={p.product_id} className="flex items-center gap-3 p-2">
                <div className="flex-1 min-w-0 text-sm"><p className="truncate">{p.name}</p><p className="text-xs text-muted-foreground">{p.head_available} at head</p></div>
                <Button type="button" size="icon" variant="outline" aria-label={`Add ${p.name}`} onClick={() => add(p)}
                  disabled={lines.some(i => i.product_id === p.product_id)} data-testid={`request-stock-add-${p.product_id}`}><Plus size={16} /></Button>
              </div>
            ))}
          </div>
          {lines.map(line => (
            <div key={line.product_id} className="flex items-center gap-2" data-testid={`request-stock-line-${line.product_id}`}>
              <span className="flex-1 min-w-0 truncate text-sm">{line.name}</span>
              <Input type="number" min="1" step="1" className="w-20" aria-label={`Quantity for ${line.name}`} value={line.quantity}
                onChange={e => setLines(l => l.map(i => (i.product_id === line.product_id ? { ...i, quantity: e.target.value } : i)))}
                data-testid={`request-stock-quantity-${line.product_id}`} />
              <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${line.name}`}
                onClick={() => setLines(l => l.filter(i => i.product_id !== line.product_id))} data-testid={`request-stock-remove-${line.product_id}`}><Trash2 size={16} /></Button>
            </div>
          ))}
          <div className="space-y-1">
            <Label htmlFor="request-stock-note">Note (optional)</Label>
            <Input id="request-stock-note" maxLength={1000} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Running out before the weekend" data-testid="request-stock-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="request-stock-cancel">Cancel</Button>
            <Button onClick={send} disabled={busy || !lines.length || invalid} data-testid="request-stock-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
